// Helpers compartilhados para templates Meta (agendamento / Follow UP)

export const getOfficialTemplateCategoryMeta = (category) => {
  const key = String(category || "UTILITY").toUpperCase();
  if (key === "MARKETING") {
    return {
      key: "MARKETING",
      label: "Marketing",
      color: "#c2185b",
      backgroundColor: "#fce4ec",
    };
  }
  if (key === "AUTHENTICATION") {
    return {
      key: "AUTHENTICATION",
      label: "Serviço",
      color: "#2e7d32",
      backgroundColor: "#e8f5e9",
    };
  }
  return {
    key: "UTILITY",
    label: "Utilidade",
    color: "#1565c0",
    backgroundColor: "#e3f2fd",
  };
};

export const escapeRegExp = (s) =>
  String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const extractPlaceholdersFromText = (value) => {
  if (!value || typeof value !== "string") return [];
  const regex = /{{\s*([^}]+)\s*}}/g;
  const results = [];
  let match;
  while ((match = regex.exec(value)) !== null) {
    const token = String(match[1] || "").trim();
    if (token) results.push(token);
  }
  return [...new Set(results)];
};

export const emptyOficialVarDescriptors = () => ({
  header: [],
  body: [],
  footer: [],
  buttons: [],
});

export const extractOfficialTemplateVariables = (components) => {
  const variables = emptyOficialVarDescriptors();
  (components || []).forEach((component) => {
    const type = String(component?.type || "").toLowerCase();
    if (!type || !variables[type]) return;
    const text = component?.text || "";
    const placeholders = extractPlaceholdersFromText(text);
    placeholders.forEach((token, idx) => {
      variables[type].push({
        type: "text",
        token,
        prompt: `${type.toUpperCase()} {{${token}}}`,
        index: idx,
      });
    });
    if (type === "buttons" && component?.buttons) {
      let buttons = [];
      try {
        buttons =
          typeof component.buttons === "string"
            ? JSON.parse(component.buttons)
            : component.buttons;
      } catch (_e) {
        buttons = [];
      }
      (buttons || []).forEach((button, buttonIndex) => {
        const sourceText = String(
          button?.url || button?.text || button?.example || ""
        );
        extractPlaceholdersFromText(sourceText).forEach((token, idx) => {
          variables.buttons.push({
            type: button?.type || "text",
            token,
            prompt: `BUTTON ${buttonIndex + 1} {{${token}}}`,
            index: idx,
            buttonIndex,
          });
        });
      });
    }
  });
  return variables;
};

export const countOfficialTemplateVariables = (descriptors) =>
  Object.values(descriptors || {}).reduce(
    (acc, list) => acc + (Array.isArray(list) ? list.length : 0),
    0
  );

export const hasEmptyOfficialTemplateValues = (descriptors, values) =>
  Object.keys(descriptors || {}).some((componentType) =>
    (descriptors[componentType] || []).some((_, index) => {
      const value = values?.[componentType]?.[index]?.value;
      return !String(value || "").trim();
    })
  );

export const buildOfficialSendComponents = (
  listingComponents,
  varValues,
  descriptors
) => {
  const out = [];
  const desc =
    descriptors || extractOfficialTemplateVariables(listingComponents);

  if ((desc.header || []).length > 0) {
    const headerComp = (listingComponents || []).find(
      (c) => String(c.type || "").toUpperCase() === "HEADER"
    );
    const format = String(headerComp?.format || "TEXT").toUpperCase();
    out.push({
      type: "header",
      parameters: desc.header.map((d, i) => {
        const val = String(varValues?.header?.[i]?.value || "").trim();
        const named =
          d.token && !/^\d+$/.test(String(d.token))
            ? { parameter_name: String(d.token) }
            : {};
        if (format === "IMAGE") return { type: "image", image: { link: val }, ...named };
        if (format === "VIDEO") return { type: "video", video: { link: val }, ...named };
        if (format === "DOCUMENT")
          return { type: "document", document: { link: val }, ...named };
        return { type: "text", text: val, ...named };
      }),
    });
  }

  if ((desc.body || []).length > 0) {
    out.push({
      type: "body",
      parameters: desc.body.map((d, i) => {
        const val = String(varValues?.body?.[i]?.value || "").trim();
        const named =
          d.token && !/^\d+$/.test(String(d.token))
            ? { parameter_name: String(d.token) }
            : {};
        return { type: "text", text: val, ...named };
      }),
    });
  }

  if ((desc.buttons || []).length > 0) {
    const byButton = {};
    desc.buttons.forEach((d, valueIndex) => {
      const bi = d.buttonIndex ?? 0;
      if (!byButton[bi]) byButton[bi] = [];
      byButton[bi].push({ descriptor: d, valueIndex });
    });
    Object.keys(byButton).forEach((bi) => {
      const items = byButton[bi];
      const firstType = String(items[0].descriptor.type || "URL").toUpperCase();
      const subType =
        firstType === "COPY_CODE"
          ? "copy_code"
          : firstType === "URL"
            ? "url"
            : firstType.toLowerCase();
      out.push({
        type: "button",
        sub_type: subType,
        index: String(bi),
        parameters: items.map(({ valueIndex, descriptor }) => {
          const val = String(
            varValues?.buttons?.[valueIndex]?.value || ""
          ).trim();
          if (String(descriptor.type || "").toUpperCase() === "COPY_CODE") {
            return { type: "coupon_code", coupon_code: val };
          }
          return { type: "text", text: val };
        }),
      });
    });
  }

  return out;
};

export const hydrateOfficialVarValuesFromSendComponents = (
  sendComponents,
  descriptors
) => {
  const values = {};
  const list = Array.isArray(sendComponents) ? sendComponents : [];
  const findParams = (type) => {
    const comp = list.find((c) => String(c?.type || "").toLowerCase() === type);
    return Array.isArray(comp?.parameters) ? comp.parameters : [];
  };

  if ((descriptors.body || []).length) {
    values.body = {};
    findParams("body").forEach((p, i) => {
      values.body[i] = { value: p?.text || "" };
    });
  }
  if ((descriptors.header || []).length) {
    values.header = {};
    findParams("header").forEach((p, i) => {
      const val =
        p?.text ||
        p?.image?.link ||
        p?.video?.link ||
        p?.document?.link ||
        "";
      values.header[i] = { value: val };
    });
  }
  if ((descriptors.buttons || []).length) {
    values.buttons = {};
    const buttonComps = list.filter(
      (c) => String(c?.type || "").toLowerCase() === "button"
    );
    let flatIdx = 0;
    buttonComps.forEach((comp) => {
      (comp.parameters || []).forEach((p) => {
        values.buttons[flatIdx] = {
          value: p?.text || p?.coupon_code || "",
        };
        flatIdx += 1;
      });
    });
  }
  return values;
};

export const applyOfficialPreviewSubstitution = (text, descriptors, values) => {
  let out = text || "";
  (descriptors || []).forEach((d, i) => {
    const val = values?.[i]?.value;
    if (val == null || !String(val).trim()) return;
    out = out.replace(
      new RegExp(`\\{\\{\\s*${escapeRegExp(d.token)}\\s*\\}\\}`, "g"),
      String(val)
    );
  });
  return out;
};
